import { Component, Inject, Input, Output, EventEmitter, OnInit } from 'ng-metadata/core';

import './source-avatar.component.scss';

/**
 * @ngdoc directive
 * @name mwContentDocumentSourceAvatar
 * @module mw.components.contentDocuments
 *
 * @param {string} [link-url] - The url of the document
 * @param {string} [image-url] - The URL of the avatar
 * @param {string} [source-name] - Name of the source, required for avatar generation if favicon doesn't exist
 * @param {string} [document-title] - Alt title of the avatar image.
 * @param {boolean} [is-rounded] - Flag to tell if the avatar is rounded or not.
 * @param {number} [initials-size=1] - Determine the number of initials in the avatar, can be 1 or 2, in case of 2 characters, the source-name must have the firstname and lastname
 * @param {expression} [on-click] - Angular expression to evaluate when the avatar is clicked.  $event is the ng-click event.
 *
 * @description
 * `<mw-content-document-source-avatar`> is a component that renders the avatar for a content-document component
 *
 * @usage
 * <hljs hljs-language="html">
 *  <mw-source-avatar title="some-title" link-url="https://www.google.com" image-url="http://some-url.com/cool.png"> </mw-source-avatar>
 * </hljs>
 */
@Component({
    selector: 'mw-content-document-source-avatar',
    template: `
        <a ng-href="{{::$ctrl.linkUrl}}"
            target="_blank"
            class="mw-content-document-source-avatar" target="_blank"
            data-tracking-event="documentOperation/open"
            ng-click="$ctrl.handleAvatarClick($event);"
            ng-class="{'mw-content-document-source-avatar--rounded': $ctrl.isRounded, 'mw-content-document-source-avatar--loaded': !$ctrl.isLoading, 'mw-content-document-source-avatar--has-favicon': $ctrl.faviconLoaded, 'mw-content-document-source-avatar--no-favicon': !$ctrl.faviconLoaded}">
            <img class="mw-content-document-source-avatar__image"
                ng-src="{{::$ctrl.imageUrl}}"
                alt="{{::$ctrl.documentTitle | mwStripTags}}"
                title="{{::$ctrl.sourceName | mwStripTags}}"></img>
            <div class="mw-content-document-source-avatar__placeholder"
                title="{{::$ctrl.sourceName | mwStripTags}}">
                <span ng-class="::{'mw-content-document-source-avatar__2-initials': $ctrl.initialsSize === 2 }">{{::$ctrl.sourceFirstLetters}}</span>
            </div>
            <div ng-if="::$ctrl.mediaType && $ctrl.mediaType !== 'news'" class="mw-content-document-source-avatar__media-type-icon">
                <mw-icon class="mw-content-document-media-type-icon" svg-src="{{$ctrl.mediaTypeIconUrl}}" size="xs"></mw-icon>
            </div>

        </a>
        `
})
export class SourceAvatarComponent implements OnInit {
    @Input('<') linkUrl: string;
    @Input('<') imageUrl: string;
    @Input('<') documentTitle: string;
    @Input('<') sourceName: string;
    @Input('<') isRounded: boolean = false;
    @Input('<') initialsSize: number = 1;
    @Input('<') mediaType: string;
    @Input('<') mediaTypeIcon: string;

    @Output() onClick = new EventEmitter<ng.IAngularEvent>();

    sourceFirstLetters: string;
    faviconLoaded: boolean = false;
    isLoading: boolean = true;
    mediaTypeIconUrl: string;

    constructor(
        @Inject('$scope') private $scope: ng.IScope,
        @Inject('$window') private $window: ng.IWindowService,
        @Inject('$translate') private $translate,
        @Inject('mwEnvDetector') private mwEnvDetector
    ) {}

    ngOnInit() {
        if (this.sourceName) {
            if (this.sourceName === 'Unknown') {
                this.sourceFirstLetters = '?';
            } else {
                let firstLetterIndex = 0;

                if (this.sourceName.charAt(0) === '@') {
                    firstLetterIndex = 1;
                }

                this.sourceFirstLetters = this.sourceName.charAt(firstLetterIndex).toUpperCase();

                if (this.initialsSize === 2) {
                    let splitSourceName = this.sourceName.split(' ');
                    if (splitSourceName.length > 1) {
                        this.sourceFirstLetters += splitSourceName[1].charAt(0).toUpperCase();
                    }
                }
            }
        }

        this.loadImage(this.imageUrl);

        this.mediaTypeIcon = this.mediaTypeIcon || this.mediaType;
        if (this.mediaType) {
            let mediaTypeSvgName = this.mediaTypeIcon + '_color';
            if (this.mediaTypeIcon === 'instagram' && (this.mwEnvDetector.browser === 'ie' || this.mwEnvDetector.browser === 'ms-edge' || this.mwEnvDetector.browser === 'safari')) {
                mediaTypeSvgName += '_simple';
            }

            this.mediaTypeIconUrl = `https://assets.meltwater.io/flux-icons/latest/mediatypes/svg/${mediaTypeSvgName}.svg`;
        }

    }

    loadImage(url: string) {

        if (url) {
            let img = this.$window.document.createElement('img');

            img.onload = () => {
                if (img.naturalHeight >= 48 && img.naturalWidth >= 48) {
                    this.faviconLoaded = true;
                }

                this.isLoading = false;
                this.$scope.$evalAsync();
                img.remove();
            };
            img.onerror = () => {
                this.isLoading = false;
                this.$scope.$evalAsync();
                // the image has failed
            };
            img.src = url;
        } else {
            this.isLoading = false;
        }
    }

    handleAvatarClick(event: ng.IAngularEvent) {
        this.onClick.emit(event);
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/content-documents/shared/source-avatar/source-avatar.component.ts